/**
 * Finance benchmarks for remodelers / design-build firms.
 *
 * These thresholds drive the color coding and coaching cues across the Finance
 * dashboard widgets. They are intentionally simple and configurable so the
 * coaching team can tune them without touching widget code.
 *
 * Each benchmark defines `good` / `ok` cutoffs and a direction:
 *  - higherIsBetter: values at/above `good` are healthy
 *  - !higherIsBetter (lower is better): values at/below `good` are healthy
 */

export const FINANCE_BENCHMARKS = {
  gpm: { label: 'Gross Profit Margin', unit: '%', good: 35, ok: 25, higherIsBetter: true,
    target: '35-50% for design-build' },
  npm: { label: 'Net Profit Margin', unit: '%', good: 10, ok: 5, higherIsBetter: true,
    target: '8-12% is healthy' },
  overhead: { label: 'Overhead', unit: '%', good: 25, ok: 35, higherIsBetter: false,
    target: 'Keep under ~30% of revenue' },
  runway: { label: 'Project Runway', unit: 'mo', good: 6, ok: 3, higherIsBetter: true,
    target: '3+ months of cash on hand' },
  currentRatio: { label: 'Current Ratio', unit: 'x', good: 1.5, ok: 1.0, higherIsBetter: true,
    target: 'Above 1.5x' },
  debtToEquity: { label: 'Debt-to-Equity', unit: 'x', good: 1, ok: 2, higherIsBetter: false,
    target: 'Below 2x' }
};

/**
 * Status keys: 'good' | 'ok' | 'poor' | 'none'
 */
export const getMetricStatus = (metricKey, value) => {
  const b = FINANCE_BENCHMARKS[metricKey];
  if (!b || value == null || Number.isNaN(value)) return 'none';
  if (b.higherIsBetter) {
    if (value >= b.good) return 'good';
    if (value >= b.ok) return 'ok';
    return 'poor';
  }
  if (value <= b.good) return 'good';
  if (value <= b.ok) return 'ok';
  return 'poor';
};

/**
 * Tailwind class bundle for a given status, used for text/badges/accents.
 * Dark-mode aware via the `dark:` variants already baked in.
 */
export const STATUS_STYLES = {
  good: {
    text: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    dot: 'bg-emerald-500',
    stroke: '#10b981',
    label: 'On track'
  },
  ok: {
    text: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    dot: 'bg-amber-500',
    stroke: '#f59e0b',
    label: 'Watch'
  },
  poor: {
    text: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    dot: 'bg-red-500',
    stroke: '#ef4444',
    label: 'Needs attention'
  },
  none: {
    text: 'text-gray-500 dark:text-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
    dot: 'bg-gray-400',
    stroke: '#9ca3af',
    label: 'No data'
  }
};

export const statusStyle = (status) => STATUS_STYLES[status] || STATUS_STYLES.none;

/**
 * Short coaching cue for a metric + status, shown under widget values.
 */
export const getCoachingCue = (metricKey, status) => {
  const b = FINANCE_BENCHMARKS[metricKey];
  if (!b) return '';
  if (status === 'good') return `Healthy. Target: ${b.target}.`;
  if (status === 'ok') return `Close to target. Aim for ${b.target}.`;
  if (status === 'poor') return `Below target. Goal: ${b.target}.`;
  return `Target: ${b.target}.`;
};

/**
 * Source badge metadata: where a metric value came from.
 * source: 'quickbooks' | 'jobtread' | 'manual' | 'none'
 */
export const SOURCE_BADGES = {
  quickbooks: { label: 'QuickBooks', classes: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  jobtread: { label: 'JobTread', classes: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  googleanalytics: { label: 'Google Analytics', classes: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  manual: { label: 'Your inputs', classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  none: { label: 'No data', classes: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' }
};

export const sourceBadge = (source) => SOURCE_BADGES[source] || SOURCE_BADGES.none;
